import { useState, useEffect } from 'react';
import {
  Card,
  Group,
  Text,
  Stack,
  ThemeIcon,
  Center,
  Loader,
} from '@mantine/core';
import { Book } from 'react-feather';
import { coachPortalApi, type CoachResource } from '../../../services/api/coachPortal';
import { ResourceRow } from './ResourceRow';

export interface ResourcesTabContentProps {
  cohortId: string;
}

export function ResourcesTabContent({ cohortId }: ResourcesTabContentProps) {
  const [resources, setResources] = useState<CoachResource[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const result = await coachPortalApi.getCohortResources(cohortId);
        setResources(result.data);
      } catch {
        // Silently handle - will show empty state
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [cohortId]);

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  if (resources.length === 0) {
    return (
      <Card withBorder p="xl">
        <Stack align="center" gap="md">
          <ThemeIcon size={48} radius="xl" variant="light" color="gray">
            <Book size={24} />
          </ThemeIcon>
          <Text fw={600}>No Resources</Text>
          <Text size="sm" c="dimmed" ta="center">
            No resources are available for this cohort yet.
          </Text>
        </Stack>
      </Card>
    );
  }

  return (
    <Card withBorder>
      <Group justify="space-between" mb="md">
        <Text fw={600} size="sm">
          {resources.length} Resource{resources.length !== 1 ? 's' : ''}
        </Text>
      </Group>
      <Stack gap={0}>
        {resources.map((resource) => (
          <ResourceRow key={resource.id} resource={resource} />
        ))}
      </Stack>
    </Card>
  );
}
